import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "../util/axios";
import { notification } from "antd";
import { formValues, UpdateResourceConfigDto } from "../types/resource";
import { useNavigate } from "react-router-dom";
import { Resource } from "../components/ResourceTable/ResourceTable.types";
import { useTranslation } from "react-i18next";
import { ResourceConfig } from "../types/resource-config";
import { AxiosError } from "axios";

export const useGetResources = () =>
  useQuery<Resource[]>({
    queryKey: ["resourcesList"],
    queryFn: () => axiosInstance.get("/resource/overview").then((w) => w.data),
    initialData: [],
  });

export const useAddResource = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (data: formValues) =>
      axiosInstance.post(`/resource/create`, data),
    onSuccess: () => {
      notification.success({
        message: t("toast.resource-created"),
        description: t("toast.resource-created-description"),
      });
      navigate("/portal/resources");
    },
    onError: () => {
      notification.error({
        message: t("toast.failed-to-create-resource"),
        description: t("toast.please-check-your-credentials"),
      });
    },
  });
};

export const useDeleteResource = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (resourceId: string) =>
      axiosInstance.delete(`/resource/${resourceId}`),
    onSuccess: () => {
      notification.success({
        message: t("toast.resource-deleted"),
        description: t("toast.resource-deleted-description"),
      });
      return queryClient.invalidateQueries({ queryKey: ["resourcesList"] });
    },
    onError: () => {
      notification.error({
        message: t("toast.failed-to-delete-resource"),
        description: t("toast.please-check-your-credentials"),
      });
    },
  });
};

// To sync resources from providers
export const useSyncResources = (options: { enabled: boolean }) => {
  const { t } = useTranslation();
  return useQuery({
    queryKey: ["resourceSync"],
    queryFn: () =>
      axiosInstance.get("/resource/sync").catch((error) => {
        notification.error({
          message: t("toast.failed-to-sync-resources"),
          description: error.response.data.message,
        });
      }),
    enabled: options.enabled,
  });
};

export const useGetResourceConfig = (resourceId: string) =>
  useQuery<ResourceConfig>({
    queryKey: ["resourceConfig", resourceId],
    queryFn: () =>
      axiosInstance.get(`/resource/config/${resourceId}`).then((w) => w.data),
    enabled: !!resourceId,
  });

export const useEditResourceConfig = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (data: UpdateResourceConfigDto) =>
      axiosInstance.patch(`/resource/config/${data.resourceId}`, data),
    onSuccess: () => {
      notification.success({
        message: t("toast.resource-updated"),
        description: t("toast.resource-updated-description"),
      });
      queryClient.invalidateQueries({ queryKey: ["resourcesList"] });
      return navigate("/portal/resources");
    },
    onError: (error: AxiosError<AxiosError>) => {
      if (error.response?.data?.message == "locale must be locale") {
        notification.error({
          message: t("toast.failed-to-update-resource"),
          description: t("toast.invalid-locale"),
        });
        return;
      }
      notification.error({
        message: t("toast.failed-to-update-resource"),
        description: t("toast.please-check-your-credentials"),
      });
    },
  });
};
